<template>
  <div>
    <v-list dense class="pa-0 mx-0">
      <div v-if="filterToggle == filterToggles.selected && selectionCount == 0" class="pa-4 text-xs-center">
        <v-icon color="warning">warning</v-icon><br />
        No {{ entityName }} selected
      </div>
      <VirtualList v-show="!(filterToggle == filterToggles.selected && selectionCount == 0)" ref="virtualScroll" :size="40" :remain="6">
        <template v-for="(item, index) in combinedItems">
          <template v-if="item.type === 0 && hasCategory">
            <v-list-tile :key="`category-${index}`" @click="toggleExpand(item)">
              <v-list-tile-action style="min-width: 0px;">
                <v-checkbox color="primary" :input-value="isSelected(item)" @click.stop="toggleCategory(item)" hide-details :indeterminate="isPartialSelection(item)" />
              </v-list-tile-action>
              <v-list-tile-avatar v-if="!hideCategoryIcons" class="pl-0" style="min-width: 0px;">
                <v-icon>{{ item.icon || getIcon(item.name) }}</v-icon>
              </v-list-tile-avatar>
              <v-list-tile-content style="flex-grow: 1" v-text="item.name" />
              <v-list-tile-content class="font-weight-light grey--text text--darken-0" style="flex-grow: 0;">
                <v-chip color="rgba(0,0,0,.07)" small>
                  <span :class="{ 'error--text': item.count == 0}">
                    {{ item.count }}
                  </span>
                </v-chip>
              </v-list-tile-content>
              <v-list-tile-action class="ml-2" style="min-width: 0;">
                <v-icon>{{ item.open || isSearching ? 'arrow_drop_up' : 'arrow_drop_down'}}</v-icon>
              </v-list-tile-action>
            </v-list-tile>
          </template>
          <template v-else-if="item.type !== 0">
            <v-list-tile :key="`item-${index}`" @click="toggleStation(item.id)">
              <v-list-tile-avatar v-if="!hideCategoryIcons" style="min-width: 0px;" />
              <v-list-tile-action style="min-width: 0px;">
                <v-checkbox @click.prevent primary color="primary" :input-value="getStationValue(item)" hide-details />
              </v-list-tile-action>
              <v-list-tile-content>
                <v-list-tile-title class="font-weight-thin">{{ item[entityNameKey] }}
                </v-list-tile-title>
                <v-list-tile-sub-title v-html="item.description"></v-list-tile-sub-title>
              </v-list-tile-content>
            </v-list-tile>
          </template>
        </template>
      </VirtualList>
    </v-list>
  </div>
</template>

<script>
  import virtualList from 'vue-virtual-scroll-list'


  import {
    debounce,
  } from '@/helpers/performance'

  import {
    stringSorter,
  } from '@/helpers/sorting'

  const defaultDebounceDelay = 500

  const queryTypes = {
    category: 0,
    entity: 1,
  }

  const DEFAULT_CATEGORY = 'default'

  export default {
    components: {
      VirtualList: virtualList,
    },
    props: {
      filterToggle: Number,
      filterToggles: Object,
      selectionCount: Number,
      search: String,
      entity: Array,
      categoryIcons: {
        type: Object,
        default: () => ({
          [DEFAULT_CATEGORY]: '',
        }),
      },
      missingItem: Array,
      itemName: {
        type: String,
        default: 'Entity',
      },
      entityNameKey: {
        type: String,
        default: 'name',
      },
      hideCategoryIcons: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        initialized: false,
        queryTypes,
        loading: {
          items: false,
        },
        searching: false,
        selected: {},
        menuProps: {
          closeOnContentClick: false,
        },
        menu: {
          show: false,
        },
        categories: {},
        hasCategory: false,
      }
    },
    computed: {
      _showCategories() {
        return this.hasCategory
      },
      _missingStations: {
        get() {
          return this.missingItem
        },
        set(entity) {
          this.$emit('update:missingItem', entity)
        },
      },
      combinedItems() {
        let items = []

        if (this.filterToggle == this.filterToggles.selected) {
          const selectedCategory = {
            ignore: true,
            name: 'Various',
            icon: 'help',
            open: true,
            count: this.selectionCount,
            type: this.queryTypes.category,
          }

          items.push(selectedCategory)
          this.sortedCategoryKeys.forEach(key => {
            let category = this.categories[key]

            category.shownItems.forEach(entity => {
              if (this.selected[key][entity.id]) items.push(entity)
            })
          })
        } else {
          this.sortedCategoryKeys.forEach(key => {
            let category = this.categories[key]
            if (category.shownItems.length === 0) return

            items.push(category)
            if (category.open || this.isSearching || !this.hasCategory) {
              items.push(...category.shownItems)
            }
          })
        }

        return items
      },
      sortedCategoryKeys() {
        return Object.keys(this.categories).sort(stringSorter)
      },
      filteredStationCategories() {
        let result = {}
        Object.entries(this.categories).forEach(([key, category]) => {
          if (category.show) result[key] = category
        })

        return result
      },
      isSearching() {
        return this.search && !this.loading.items
      },
      queryResults() {
        return debounce(function(value) {
          this.filteredItems = this.updateItems(value)
          this.searching = false
          this.menu.show = true
        }, defaultDebounceDelay)
      },
    },
    watch: {
      filterToggle() {
        this.forceRender()
      },
      entity: {
        immediate: true,
        handler(value) {
          this.makeCategories(value)
          this.queryResults('')
        },
      },
      categories(val, oldValue) {
        if (Object.keys(oldValue).length === 0 && Object.keys(val).length !== 0) {
          this.$nextTick(() => this.updateItems())
        }
      },
      searching(value) {
        this.$emit('isSearching', value)
      },
      search(value) {
        this.searching = true
        this.queryResults(value)
      },
      selected: {
        deep: true,
        handler: function(value) {
          let selection = value

          if (!this.hasCategory) {
            selection = Object.values(selection[DEFAULT_CATEGORY])
          }

          this.$emit('selected', selection)
        },
      },
    },
    methods: {
      makeCategories(entity) {
        if (!entity) return

        this.initialized = true
        let categories = {}

        this.hasCategory = entity.every(entity => entity.category)

        entity.forEach(entity => {
          let categoryKey = this.getEntityCategory(entity)
          let normalizedCategoryKey = categoryKey.toLowerCase()


          if (!categories[normalizedCategoryKey]) {
            categories[normalizedCategoryKey] = {
              name: categoryKey,
              normalizedName: normalizedCategoryKey,
              type: queryTypes.category,
              entity: [],
              shownItems: [],
              open: false,
              show: true,
              count: 0,
            }
          }

          categories[normalizedCategoryKey].entity.push(entity)
        })


        this.categories = categories
        let selected = {}
        Object.keys(categories).forEach(key => selected[key] = {})
        this.selected = selected
      },
      getIcon(categoryName) {
        let category = this.categoryIcons[categoryName]
        if (!category) return 'help'
        return category.IconPath
      },
      isPartialSelection(category) {
        if (category.ignore) return false

        let selection = this.selected[category.name]
        let keyLength = Object.keys(selection).length
        return keyLength > 0 && keyLength != this.categories[category.name].count
      },
      isSelected(category) {
        if (category.ignore) return true

        let selection = this.selected[category.name]
        let keyLength = Object.keys(selection).length
        return keyLength > 0
      },
      toggleExpand(category) {
        category.open = !category.open

        if (!this.search) {
          this.updateItems(this.search)
        }
      },
      updateItems(searchValue) {
        if (!this.categories) return

        const normalizedValue = searchValue && searchValue.toLowerCase() || null
        Object.keys(this.categories).forEach(key => {
          let category = this.categories[key]

          const matchesCategory = category.normalizedName.includes(normalizedValue)
          if (normalizedValue && !matchesCategory) {
            category.shownItems = category.entity.filter(entity =>
              entity.normalizedName.includes(normalizedValue))
            category.show = category.shownItems.length > 0
            category.count = category.shownItems.length
          } else {
            category.show = true
            category.shownItems = category.entity
          }

          category.count = category.shownItems.length
        })

        this.loading = {
          ...this.loading,
          items: false,
        }
      },
      toggleCategory(category) {
        if (category.ignore) {
          this.untoggleAll()
          return
        }

        let selected = this.selected[category.name]
        if (Object.keys(selected).length < category.count) {
          category.shownItems.forEach(item => {
            this.$set(this.selected[category.name], item.id, item)
          })
        } else {
          category.entity.forEach(item => {
            this.$delete(selected, item.id)
          })
        }
      },
      makeEmptySelection() {
        let selection = {}
        Object.keys(this.selected).forEach(category => {
          selection[category] = {}
        })

        return selection
      },
      untoggleAll() {
        if (this.selectionCount == 0) return
        let selection = this.makeEmptySelection()
        this.selected = selection
      },
      getEntityCategory(entity) {
        return this.hasCategory ? entity.category : DEFAULT_CATEGORY
      },
      toggleStation(id) {
        let item = this.entity.find(entity => entity.id == id)
        let itemCategory = this.getEntityCategory(item)

        if (this.selected[itemCategory][item.id]) {
          this.$delete(this.selected[itemCategory], item.id)
        } else {
          this.$set(this.selected[itemCategory], item.id, item)
        }
      },
      async selectIds(entityIds) {
        if (!entityIds) {
          this.untoggleAll()
          return
        }

        let selected = this.makeEmptySelection()
        let missingStationIds = []
        entityIds.forEach(id => {
          let entity = this.entity.find(s => s.id == id)
          let entityCategory = this.getEntityCategory(entity)
          if (!entity) {
            missingStationIds.push(id)
          } else {
            selected[entityCategory][entity.id] = entity
          }
        })

        // Trigger DOM update all at once
        this.selected = selected

        this._missingStations = missingStationIds

        await this.$nextTick()
        this.forceRender()
      },
      forceRender() {
        this.$refs.virtualScroll.forceRender()
      },
      getStationValue(item) {
        return !!this.selected[this.getEntityCategory(item)][item.id]
      },
    },
  }
</script>